import React, { useState, useEffect } from 'react';
import './OrderList.css';

const OrderList = ({ bcuCode, systemYN }) => {
  const today = new Date().toISOString().split('T')[0];  // 오늘 날짜 (yyyy-mm-dd 형식)

  const [orders, setOrders] = useState([]);
  const [formData, setFormData] = useState({
    orderDateFrom: today,  // 기본값 오늘 날짜
    orderDateTo: today,    // 기본값 오늘 날짜
    orderCustomer: '',     // 거래처
    orderNumber: '',       // 주문번호
    patientName: '',       // 환자명
    bcu_code: bcuCode,     // 로그인한 ID는 bcu_code에 설정
  });

  const [error, setError] = useState('');

  // 서버 URL 설정 (localhost와 배포된 서버 구분)
  const serverUrl =
    window.location.hostname === "localhost" ||
    window.location.hostname === "192.168.0.254"
      ? `http://${window.location.hostname}:4000`
      : `${window.location.protocol}//smes.izenimplant.com`;

  const handleSearch = async () => {
    const { orderDateFrom, orderDateTo, orderCustomer, orderNumber, patientName } = formData;

    let url = `${serverUrl}/api/orderlist`;  // 서버 URL을 적용한 기본 URL
    
    // URLSearchParams로 쿼리 파라미터 생성
    const params = new URLSearchParams();
    params.append('bcu_code', bcuCode);  // 로그인한 ID (bcu_code)

    // 날짜 필터 추가
    if (orderDateFrom) params.append('fromDate', orderDateFrom);
    if (orderDateTo) params.append('toDate', orderDateTo);

    // 주문번호 및 환자명 추가 (비어 있으면 ''로 던짐)
    params.append('orderCustomer', orderCustomer === '' ? '%%' : orderCustomer);  // 빈 문자열이면 그대로 ''
    params.append('orderNum', orderNumber === '' ? '%%' : orderNumber);  // 빈 문자열이면 그대로 ''
    params.append('patientName', patientName === '' ? '%%' : patientName);  // 빈 문자열이면 그대로 ''

    // 최종적으로 URL에 쿼리 문자열 추가
    url += `?${params.toString()}`;

    try {
      const res = await fetch(url);
      if (!res.ok) {
        throw new Error('주문 목록 조회 실패');
      }
      const data = await res.json();
      setOrders(data);  // 데이터 상태 업데이트
    } catch (err) {
      console.error(err);
      setError('데이터를 불러오는 중 오류 발생');
    }
  };

  useEffect(() => {
    handleSearch();
  }, []);

  // 폼 필드 값 변경 처리
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // 삭제 함수
  const handleDelete = (order) => {
    const isConfirmed = window.confirm(`삭제하시겠습니까?\n주문번호 : ${order.SWM_CODE}\n순번 : ${order.SER}`);
    if (!isConfirmed) return;

    fetch(`${serverUrl}/api/orderlist/${order.SWD_CODE}?userId=${encodeURIComponent(order.SWD_CODE)}`, {
      method: 'DELETE',
    })
      .then((res) => {
        if (!res.ok) throw new Error('삭제 실패');
        setOrders((prev) => prev.filter((o) => o.SWD_CODE !== order.SWD_CODE));
        handleSearch();
        setError('');
      })
      .catch((err) => {
        console.error(err);
        setError('접수 완료된 주문입니다. 관리자에게 문의하세요.');
      });
  };

  return (
    <div className="order-technician-container">
      <h2>주문 조회</h2>
      {/* 조회 조건 (상단) */}
      <div className="orderlist-form-section">
        <div className="orderlist-header">
          <h3>조회 조건</h3>
          <button className="submit-button" onClick={handleSearch}>조회</button>
        </div>
        <form onSubmit={(e) => e.preventDefault()} className="orderlist-form">
          <div className="form-row">
            <label htmlFor="orderDateFrom">주문일</label>
            <input
              type="date"
              name="orderDateFrom"
              id="orderDateFrom"
              value={formData.orderDateFrom}
              onChange={handleChange}
              onClick={(e) => e.target.showPicker && e.target.showPicker()} // 클릭 시 달력 표시
            />
            <span className="to-label">-</span>
            <input
              type="date"
              name="orderDateTo"
              id="orderDateTo"
              value={formData.orderDateTo}
              onChange={handleChange}
              onClick={(e) => e.target.showPicker && e.target.showPicker()} // 클릭 시 달력 표시
            />
          </div>
          {systemYN === 'Y' && (
            <div className="form-row">
              <label htmlFor="orderCustomer">거래처</label>
              <input
                type="text"
                name="orderCustomer"
                id="orderCustomer"
                value={formData.orderCustomer}
                onChange={handleChange}
                placeholder="거래처 입력"
              />
            </div>
          )}
          <div className="form-row">
            <label htmlFor="orderNumber">주문번호</label>
            <input
              type="text"
              name="orderNumber"
              id="orderNumber"
              value={formData.orderNumber}
              onChange={handleChange}
              placeholder="주문번호 입력"
            />
          </div>
          <div className="form-row">
            <label htmlFor="patientName">환자명</label>
            <input
              type="text"
              name="patientName"
              id="patientName"
              value={formData.patientName}
              onChange={handleChange}
              placeholder="환자명 입력"
            />
          </div>
          {error && <div className="error-message">{error}</div>}
        </form>
      </div>

      {/* 주문 목록 */}
      <div className="orderlist-list-section">
        <h3>주문 목록</h3>
        <table className="orderlist-table">
          <thead>
            <tr>
              {systemYN === 'Y' && <th>거래처</th>}
              <th>주문번호</th>
              <th>순번</th>
              <th>주문일</th>
              <th>배송예정일</th>
              <th>기공사명</th>
              <th>환자명</th>
              <th>치식번호</th>
              <th>Implant</th>
              <th>Fixture</th>
              <th>Size</th>
              <th>Type</th>
              <th>Anodizing</th>
              <th>비고</th>
              <th>확정일</th>
              <th>진행상태</th>
              <th>삭제</th>
            </tr>
          </thead>
          <tbody>
            {orders.map((order, index) => (
              // <tr> 태그에 조건부 className 추가
              <tr
                key={index}
                className={order.ORDERSTATUS === '접수완료' ? 'completed-order' : ''}
              >
                {systemYN === 'Y' && <td>{order.BCU_NAME}</td>}
                <td>{order.SWM_CODE}</td>
                <td>{order.SER}</td>
                <td>{new Date(order.ORDERDATE).toISOString().split('T')[0]}</td>
                <td>{new Date(order.SWM_DELIVERYDATE).toISOString().split('T')[0]}</td>
                <td>{order.WORKERNAME}</td>
                <td>{order.PATIENTNAME}</td>
                <td>{order.DENTALNUM}</td>
                <td>{order.COMNAME}</td>
                <td>{order.FIXTURENAME}</td>
                <td>{order.SIZENAME}</td>
                <td>{order.TYPENAME}</td>
                <td>{order.SWD_ANODIZING}</td>
                <td>{order.SWM_REMARKS}</td>
                <td>{order.CONFIRMDATE}</td>
                <td>{order.ORDERSTATUS}</td>
                <td style={{ textAlign: 'center' }}>
                  <button onClick={() => handleDelete(order)} className="delete-button">
                    삭제
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default OrderList;
